<template>
  <div class="product-collection">
    <v-container>
      <div class="d-flex align-center mb-6">
        <v-btn
          icon
          @click="$router.go(-1)"
          class="mr-2"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h1 class="text-h4 font-weight-bold">{{ collection.name }}</h1>
      </div>
      
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      
      <div v-else-if="products.length === 0" class="text-center py-8">
        <p class="text-subtitle-1">Nenhum produto disponível nesta coleção.</p>
      </div>
      
      <div v-else>
        <v-row>
          <v-col 
            v-for="product in products" 
            :key="product.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card
              height="100%"
              hover
              @click="openProductModal(product)"
            >
              <v-img
                :src="product.image"
                height="200"
                class="white--text align-end"
              >
                <v-card-title 
                  v-if="!product.inStock" 
                  class="pa-2 red darken-2 text-caption font-weight-bold"
                  style="position: absolute; top: 10px; right: 10px; border-radius: 4px; width: auto"
                >
                  Esgotado
                </v-card-title>
              </v-img>

              <v-card-title class="text-subtitle-1 font-weight-medium pb-1 pt-3">
                {{ product.name }}
              </v-card-title>

              <v-card-text class="pt-0">
                <div class="text-h6 font-weight-bold primary--text mb-2">
                  R$ {{ product.price.toFixed(2).replace('.', ',') }}
                </div>
                <div class="text-body-2 text-truncate">
                  {{ product.description }}
                </div>
              </v-card-text>

              <v-card-actions>
                <v-btn
                  color="primary"
                  text
                  block
                >
                  <v-icon left>mdi-eye</v-icon>
                  Ver detalhes
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-container>
    
    <!-- Modal de produto -->
    <product-detail-modal 
      v-if="selectedProduct" 
      :product="selectedProduct" 
      :show="productModalOpen"
      @close="closeProductModal" 
    />
  </div>
</template>

<script>
import ProductDetailModal from '@/components/shop/products/ProductDetailModal.vue';

export default {
  name: 'ProductCollection',
  components: {
    ProductDetailModal
  },
  data() {
    return {
      collection: {},
      products: [],
      loading: true,
      selectedProduct: null,
      productModalOpen: false
    };
  },
  async created() {
    try {
      await this.fetchCollectionData();
    } catch (error) {
      console.error('Erro ao carregar dados da coleção:', error);
      this.$toast.error('Não foi possível carregar os produtos da coleção.');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchCollectionData() {
      const collectionId = this.$route.params.collectionId;
      const orgSlug = this.$route.params.orgSlug;
      
      // Simulando delay de rede
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Mockup de dados para exemplo
      this.collection = this.getMockCollection(collectionId);
      this.products = this.generateMockProducts(16, this.collection.name);
    },
    
    getMockCollection(id) {
      const collections = [
        {
          id: '1',
          name: 'Camisetas',
          description: 'Coleção de camisetas exclusivas com estampas incríveis e tecidos de alta qualidade.',
          banner: 'https://placehold.co/1200x300'
        },
        {
          id: '2',
          name: 'Acessórios',
          description: 'Acessórios únicos para complementar seu estilo com exclusividade e personalidade.',
          banner: 'https://placehold.co/1200x300'
        },
        {
          id: '3',
          name: 'Colecionáveis',
          description: 'Itens especiais em edições limitadas para verdadeiros colecionadores e fãs.',
        banner: 'https://placehold.co/1200x300'
        },
        {
          id: '4',
          name: 'Decoração',
          description: 'Produtos para decorar sua casa com estilo e mostrar sua paixão.',
          banner: 'https://placehold.co/1200x300'
        },
        {
          id: '5',
          name: 'Papelaria',
          description: 'Cadernos, canetas e outros itens de papelaria exclusivos e funcionais.',
          banner: 'https://placehold.co/1200x300'
        }
      ];
      
      const collection = collections.find(c => c.id === id);
      return collection || {
        id: id,
        name: `Coleção ${id}`,
        description: 'Uma coleção exclusiva de produtos selecionados especialmente para você.',
        banner: `https://placehold.co/1200x300`
      };
    },
    
    generateMockProducts(count, collectionType) {
      const products = [];
      let names = [];
      
      // Definir nomes com base no tipo de coleção
      switch(collectionType) {
        case 'Camisetas':
          names = [
            'Camiseta Logo Classic', 'Camiseta Vintage', 'Camiseta Estampada', 
            'Camiseta Tie-Dye', 'Camiseta Exclusiva', 'Camiseta Premium',
            'Camiseta Manga Longa', 'Camiseta Especial', 'Regata Estilizada',
            'Camiseta Oversized', 'Camiseta Cropped', 'Camiseta Listrada'
          ];
          break;
        case 'Acessórios':
          names = [
            'Boné Snapback', 'Boné Trucker', 'Boné Dad Hat', 'Pulseira Trançada',
            'Colar Personalizado', 'Anel Exclusivo', 'Chaveiro Premium', 
            'Pulseira de Silicone', 'Pin Esmaltado', 'Meia Estampada',
            'Bandana Personalizada', 'Bolsa de Ombro'
          ];
          break;
        case 'Colecionáveis':
          names = [
            'Figura de Ação', 'Miniatura Exclusiva', 'Estátua Colecionável',
            'Card Holográfico', 'Card Autografado', 'Pôster Edição Limitada',
            'Box Especial', 'Álbum de Figurinhas', 'Replica Numerada',
            'Item Autêntico', 'Modelo em Escala', 'Conjunto Comemorativo'
          ];
          break;
        case 'Decoração':
          names = [
            'Quadro Decorativo', 'Almofada Personalizada', 'Caneca Temática',
            'Porta-Retrato', 'Luminária LED', 'Vaso Decorativo',
            'Manta Exclusiva', 'Tapete Personalizado', 'Porta-Copos Set',
            'Placa Decorativa', 'Escultura de Mesa', 'Kit Decorativo'
          ];
          break;
        case 'Papelaria':
          names = [
            'Caderno Personalizado', 'Caneta Premium', 'Bloco de Notas',
            'Kit Marca-Texto', 'Agenda Exclusiva', 'Organizador de Mesa',
            'Pasta Personalizada', 'Lapiseira Premium', 'Estojo Exclusivo',
            'Post-it Personalizado', 'Régua Especial', 'Kit Escolar'
          ];
          break;
        default:
          names = [
            'Produto Exclusivo', 'Item Premium', 'Produto Especial',
            'Item Edição Limitada', 'Produto Personalizado', 'Item Colecionável',
            'Produto Temático', 'Item Autêntico', 'Produto Oficial',
            'Item Comemorativo', 'Produto Assinado', 'Item Numerado'
          ];
      }
      
      for (let i = 1; i <= count; i++) {
        const nameIndex = Math.floor(Math.random() * names.length);
        const price = collectionType === 'Colecionáveis' ? 
          Math.floor(Math.random() * 300) + 100 : 
          Math.floor(Math.random() * 100) + 20;
        
        products.push({
          id: `${collectionType.toLowerCase()}-${i}`,
          name: `${names[nameIndex]} ${i}`,
          price: price,
          image: `https://placehold.co/300x300`,
          description: `Produto exclusivo da coleção ${collectionType}. Item ${i} de alta qualidade com design único e acabamento premium.`,
          inStock: Math.random() > 0.3,
          isNewProduct: Math.random() > 0.7,
          sizes: this.getProductSizes(collectionType),
          options: this.getProductOptions(i)
        });
      }
      return products;
    },
    
    getProductSizes(collectionType) {
      if (collectionType === 'Camisetas') {
        return ['PP', 'P', 'M', 'G', 'GG', 'XGG'];
      } else if (collectionType === 'Acessórios' && Math.random() > 0.5) {
        return ['P', 'M', 'G'];
      } else if (Math.random() > 0.7) {
        return ['Único'];
      }
      return null;
    },
    
    getProductOptions(index) {
      if (index % 3 === 0) {
        return [
          { text: 'Padrão', value: 'padrao' },
          { text: 'Premium', value: 'premium' },
          { text: 'Exclusivo', value: 'exclusivo' }
        ];
      } else if (index % 5 === 0) {
        return [
          { text: 'Básico', value: 'basico' },
          { text: 'Personalizado', value: 'personalizado' }
        ];
      }
      return null;
    },
    
    openProductModal(product) {
      this.selectedProduct = product;
      this.productModalOpen = true;
    },
    
    closeProductModal() {
      this.productModalOpen = false;
      this.selectedProduct = null;
    }
  }
};
</script>

<style scoped>
.product-collection {
  padding-bottom: 40px;
}

.v-card:hover {
  transform: translateY(-5px);
  transition: transform 0.2s ease-in-out;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15) !important;
}
</style>